import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Team } from '@/api';
import { useOpenApi } from '@/common/providers/open-api-provider';
import { getFallbackErrorKey, isApiError } from '@/common/util';
import { useCompaniesTranslation } from '../hooks';
import { FETCH_TEAM_QUERY_KEY } from './company-query-keys';
import { addOrUpdateTeamCache } from './company-cache';

/**
 * A single team by id.
 *
 * The team settings pages are reachable by deep link, bookmark and refresh, so
 * they can't resolve their team out of a list page that may not contain it.
 * Every paginated list page seeds this cache, so arriving from the list costs
 * no request.
 */
export type FetchTeamResult = {
    status: 'error' | 'success' | 'pending';
    team: Team | undefined;
    /** Why the read failed, ready to show. Empty while it has not. */
    error: string;
    /** False when retrying cannot help — the team is gone, or refused. */
    canRetry: boolean;
    /**
     * The team is gone or was never visible. Separate from `error` because the
     * settings pages navigate away rather than word anything.
     */
    isNotFound: boolean;
    refetch: () => void;
};

export function useFetchTeam(teamId: string): FetchTeamResult {
    const { t } = useCompaniesTranslation();
    const { openApi } = useOpenApi();
    const openApiRef = useRef(openApi);
    useEffect(() => {
        openApiRef.current = openApi;
    });

    const queryClient = useQueryClient();

    // One place that turns a failure into a message, so every screen reading
    // this team says the same thing.
    const describeError = useCallback(
        (error: unknown): { message: string; canRetry: boolean } => {
            if (isApiError(error, 'TEAM_NOT_FOUND')) {
                return {
                    message: t('shared.errors.team_load_not_found'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'NO_PERMISSION')) {
                return {
                    message: t('shared.errors.team_load_not_found'),
                    canRetry: false
                };
            }
            if (isApiError(error, 'BAD_REQUEST')) {
                return { message: t('shared.errors.generic'), canRetry: false };
            }

            return { message: t(getFallbackErrorKey(error)), canRetry: true };
        },
        [t]
    );

    const {
        status,
        data: team,
        error,
        refetch
    } = useQuery({
        queryKey: [FETCH_TEAM_QUERY_KEY, teamId],
        queryFn: () => {
            return openApiRef.current.teams.getTeam(teamId).then((response) => {
                const team = response.data.data.team;
                addOrUpdateTeamCache(queryClient, team);
                return team;
            });
        },
        enabled: teamId !== ''
    });

    const described = error ? describeError(error) : null;

    return {
        status,
        team,
        error: described?.message ?? '',
        canRetry: described?.canRetry ?? true,
        isNotFound: isApiError(error, 'TEAM_NOT_FOUND'),
        refetch: () => {
            void refetch();
        }
    };
}
